import type { ComponentType } from 'react';
import HomeTodos from '../pages/home/HomeTodos';
import HomeCalendar from '../pages/home/HomeCalendar';
import HomeOutOfLesson from '../pages/home/HomeOutOfLesson';
import HomeDetentions from '../pages/home/HomeDetentions';
import LeadershipSummary from '../pages/home/LeadershipSummary';
import type { HomeSectionId, ModuleId, UserRole } from '../types';

/**
 * What can appear on Home, in display order.
 *
 * A school admin switches these on and off for the whole school under
 * Settings → Home page; the choice lives in
 * `schoolPrefs/{schoolId}.hiddenHomeSections` and is read through
 * `SchoolContext.activeHomeSections`.
 *
 * ── Adding a card for a new module ───────────────────────────────────────────
 *   1. add its id to `HomeSectionId` in `src/types/index.ts`
 *   2. add an entry below, with `module` set so it disappears for schools that
 *      don't have that module
 * That's it — Home renders it and the Settings toggle appears automatically.
 * Don't render a card directly from HomePage: it would bypass the toggles.
 */
export interface HomeSection {
  id: HomeSectionId;
  /** Shown on the Settings toggle. */
  name: string;
  /** One line on the Settings toggle explaining what the card does. */
  blurb: string;
  icon: string;
  Component: ComponentType;
  /** Card only shows if the school has this module. */
  module?: ModuleId;
  /** Card only shows for these roles. Omit for everyone at the school. */
  roles?: UserRole[];
}

export const HOME_SECTIONS: HomeSection[] = [
  {
    id: 'ool',
    name: 'Out of lesson',
    blurb: 'Live list of pupils out of lesson today, with a button to raise a new alert.',
    icon: '🚪',
    Component: HomeOutOfLesson,
    module: 'behaviour',
  },
  {
    id: 'detentions',
    name: 'Head Teacher Detentions today',
    blurb: 'Who is in a Head Teacher detention today and has not been marked yet.',
    icon: '⏰',
    Component: HomeDetentions,
    module: 'behaviour',
  },
  {
    id: 'leadership',
    name: 'Leadership summary',
    blurb: 'Whole-school figures for leadership — only ever visible to leadership.',
    icon: '📊',
    Component: LeadershipSummary,
    roles: ['schoolAdmin', 'headteacher'],
  },
  {
    id: 'todos',
    name: 'To do',
    blurb: 'The things needing this person’s attention, across every module.',
    icon: '📋',
    Component: HomeTodos,
  },
  {
    id: 'calendar',
    name: 'Today and coming up',
    blurb: 'Today’s lessons and the next few calendar entries.',
    icon: '📅',
    Component: HomeCalendar,
  },
];
